import React from "react";
import { Alert, Button, Divider, Input, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined, MobileOutlined } from "@ant-design/icons";
import {
  PATCH_NOTE_ITEM_META,
  PatchNoteItem,
  PatchNoteItemType,
  PatchNotes,
  emptyPatchNotes,
  hasContent,
  parsePatchNotes,
  serializePatchNotes,
} from "../../../utils/patchNotes";

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

interface Props {
  value: string;
  onChange: (value: string) => void;
}

const ITEM_TYPE_OPTIONS: { value: PatchNoteItemType; label: string }[] = [
  { value: "feature", label: `${PATCH_NOTE_ITEM_META.feature.icon} New` },
  { value: "improvement", label: `${PATCH_NOTE_ITEM_META.improvement.icon} Improved` },
  { value: "fix", label: `${PATCH_NOTE_ITEM_META.fix.icon} Fixed` },
];

const PhonePreview: React.FC<{ notes: PatchNotes | null }> = ({ notes }) => {
  const grouped = notes
    ? notes.items.reduce<Record<PatchNoteItemType, PatchNoteItem[]>>(
        (acc, item) => {
          if (item.text.trim()) acc[item.type].push(item);
          return acc;
        },
        { feature: [], improvement: [], fix: [] }
      )
    : null;

  return (
    <div
      style={{
        width: 240,
        border: "8px solid #1f1f1f",
        borderRadius: 28,
        background: "#fff",
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
      }}
    >
      <div style={{ background: "#1f1f1f", height: 18, display: "flex", justifyContent: "center", alignItems: "flex-end" }}>
        <div style={{ width: 60, height: 6, borderRadius: 4, background: "#444", marginBottom: 3 }} />
      </div>
      <div style={{ padding: "16px 14px", minHeight: 260, background: "#fafafa" }}>
        <Text strong style={{ fontSize: 15 }}>What's New</Text>
        <Divider style={{ margin: "8px 0" }} />
        {notes && hasContent(notes) ? (
          <>
            {notes.summary && (
              <Paragraph style={{ fontSize: 12, marginBottom: 12 }}>{notes.summary}</Paragraph>
            )}
            {(["feature", "improvement", "fix"] as PatchNoteItemType[]).map((type) =>
              grouped![type].length === 0 ? null : (
                <div key={type} style={{ marginBottom: 10 }}>
                  <Text style={{ fontSize: 11, color: PATCH_NOTE_ITEM_META[type].color, fontWeight: 600 }}>
                    {PATCH_NOTE_ITEM_META[type].icon} {PATCH_NOTE_ITEM_META[type].label.toUpperCase()}
                  </Text>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 16 }}>
                    {grouped![type].map((item, i) => (
                      <li key={i} style={{ fontSize: 12, marginBottom: 2 }}>{item.text}</li>
                    ))}
                  </ul>
                </div>
              )
            )}
          </>
        ) : (
          <Text type="secondary" style={{ fontSize: 12, fontStyle: "italic" }}>
            No patch notes will be shown for this update.
          </Text>
        )}
      </div>
    </div>
  );
};

const PatchNotesEditor: React.FC<Props> = ({ value, onChange }) => {
  const parsed = parsePatchNotes(value);

  const updateNotes = (patch: Partial<PatchNotes>) => {
    const base = parsed ?? emptyPatchNotes();
    onChange(serializePatchNotes({ ...base, ...patch }));
  };

  const updateItem = (index: number, patch: Partial<PatchNoteItem>) => {
    if (!parsed) return;
    const items = parsed.items.map((item, i) => (i === index ? { ...item, ...patch } : item));
    updateNotes({ items });
  };

  const addItem = () => {
    if (!parsed) return;
    updateNotes({ items: [...parsed.items, { type: "feature", text: "" }] });
  };

  const removeItem = (index: number) => {
    if (!parsed) return;
    updateNotes({ items: parsed.items.filter((_, i) => i !== index) });
  };

  const convertToStructured = () => {
    onChange(serializePatchNotes({ ...emptyPatchNotes(), summary: value ?? "" }));
  };

  const revertToPlainText = () => {
    onChange(parsed?.summary ?? "");
  };

  return (
    <Space align="start" size={20} style={{ width: "100%" }} wrap>
      <div style={{ flex: "1 1 320px", minWidth: 280 }}>
        {parsed ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Structured patch notes - this will be shown on the mobile app."
            />
            <Text strong>Summary (optional)</Text>
            <TextArea
              value={parsed.summary ?? ""}
              onChange={(e) => updateNotes({ summary: e.target.value })}
              placeholder="One or two sentences introducing this update"
              autoSize={{ minRows: 2, maxRows: 3 }}
              style={{ marginTop: 6, marginBottom: 16 }}
            />
            <Text strong>Items</Text>
            <div style={{ marginTop: 6 }}>
              {parsed.items.map((item, index) => (
                <Space key={index} style={{ display: "flex", marginBottom: 8 }} align="start">
                  <Select
                    value={item.type}
                    options={ITEM_TYPE_OPTIONS}
                    style={{ width: 120 }}
                    onChange={(type) => updateItem(index, { type })}
                  />
                  <Input
                    value={item.text}
                    placeholder="Describe the change"
                    style={{ width: 280 }}
                    onChange={(e) => updateItem(index, { text: e.target.value })}
                  />
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    disabled={parsed.items.length <= 1}
                    onClick={() => removeItem(index)}
                  />
                </Space>
              ))}
            </div>
            <Button icon={<PlusOutlined />} onClick={addItem} style={{ marginTop: 4 }}>
              Add item
            </Button>
            <div style={{ marginTop: 16 }}>
              <Button size="small" onClick={revertToPlainText}>
                Use plain text instead (won't show on mobile)
              </Button>
            </div>
          </>
        ) : (
          <>
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Plain text changelog - this will NOT be shown as patch notes on the mobile app."
              description='Only changelogs saved in the structured format are shown to mobile users. Click "Convert to structured patch notes" below to make this update visible there.'
            />
            <TextArea
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Enter changelog"
              autoSize={{ minRows: 3, maxRows: 6 }}
            />
            <Button type="dashed" onClick={convertToStructured} style={{ marginTop: 12 }}>
              Convert to structured patch notes
            </Button>
          </>
        )}
      </div>
      <div style={{ flex: "0 0 auto" }}>
        <Space style={{ marginBottom: 8 }}>
          <MobileOutlined />
          <Text type="secondary" style={{ fontSize: 12 }}>Mobile preview</Text>
        </Space>
        <PhonePreview notes={parsed} />
      </div>
    </Space>
  );
};

export default PatchNotesEditor;
